import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { PaymentLinkAPI, ThreeDSAPI } from '../services/api';
import { PaymentLinkCustomerView, CustomerPayResponse } from '../types/payment';

type PaymentStep = 'loading' | 'form' | 'processing' | '3ds_method' | 'challenge' | 'decoupled' | 'success' | 'error' | 'expired';

// Card formatting/detection for the customer card form.
// Rule: a card number starting with "3" is treated as American Express —
// 15 digits, 4-6-5 grouping, 4-digit CVV. Everything else (Visa, Mastercard,
// Discover, …) is 16 digits, 4-4-4-4 grouping, 3-digit CVV.
const getCardConfig = (digits: string): { maxLength: number; cvvLength: number; groups: number[] } => {
  if (digits.startsWith('3')) {
    return { maxLength: 15, cvvLength: 4, groups: [4, 6, 5] };
  }
  return { maxLength: 16, cvvLength: 3, groups: [4, 4, 4, 4] };
};

// Digits-only card value → grouped display string (e.g. "3782 822463 10005").
const formatCardNumber = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  const { maxLength, groups } = getCardConfig(digits);
  const trimmed = digits.slice(0, maxLength);
  const parts: string[] = [];
  let idx = 0;
  for (const g of groups) {
    if (idx >= trimmed.length) break;
    parts.push(trimmed.slice(idx, idx + g));
    idx += g;
  }
  return parts.join(' ');
};

// Raw MMYY digits → "MM/YY" for display (state stays raw MMYY).
const formatExpiryDisplay = (mmYY: string): string =>
  mmYY.length > 2 ? mmYY.slice(0, 2) + '/' + mmYY.slice(2) : mmYY;

/**
 * Full GPayments ActiveServer Browser SDK flow:
 *
 * 1. Customer submits card → POST /api/3ds/brw/init (card + amount)
 * 2. Create hidden iframe with threeDSServerCallbackUrl (browser fingerprint + 3DS Method)
 * 3. Listen for postMessage: callbackType = 3DSMethodFinished | 3DSMethodSkipped
 * 4. POST /api/3ds/brw/auth (threeDSServerTransID + optional browserInfo)
 * 5. Handle transStatus:
 *    - "Y" (frictionless): extract CAVV/ECI → submit payment
 *    - "C" (challenge): show challenge iframe, wait for AuthResultReady
 *    - "N"/"R"/"U": authentication failed/rejected/unavailable
 * 6. If challenge: GET /api/3ds/brw/result?threeDSServerTransID=...
 * 7. Submit payment via POST /api/payment-links/pay/{token} with 3DS fields
 */
export const CustomerPayment: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [step, setStep] = useState<PaymentStep>('loading');
  const [linkData, setLinkData] = useState<PaymentLinkCustomerView | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  // Card form state
  const [ccnumber, setCcnumber] = useState('');
  const [ccexp, setCcexp] = useState('');
  const [cvv, setCvv] = useState('');
  const [cardErrors, setCardErrors] = useState<Record<string, string>>({});

  // 3DS state
  const [serverTransId, setServerTransId] = useState('');
  const methodIframeRef = useRef<HTMLIFrameElement | null>(null);
  const challengeIframeRef = useRef<HTMLIFrameElement | null>(null);
  const authCalledRef = useRef(false); // Prevent double auth calls

  // Refs to hold latest card values for use in async callbacks
  const cardRef = useRef({ ccnumber: '', ccexp: '', cvv: '' });
  useEffect(() => {
    cardRef.current = { ccnumber: ccnumber.replace(/\D/g, ''), ccexp, cvv };
  }, [ccnumber, ccexp, cvv]);

  // Cap the CVV to the current brand's length (trims a 4-digit Amex CVV back to
  // 3 if the number is edited to a Visa/Mastercard, etc.).
  useEffect(() => {
    const max = getCardConfig(ccnumber.replace(/\D/g, '')).cvvLength;
    setCvv(prev => (prev.length > max ? prev.slice(0, max) : prev));
  }, [ccnumber]);

  // CVV length allowed for the currently-entered card (4 for Amex, else 3).
  const cvvMaxLength = getCardConfig(ccnumber.replace(/\D/g, '')).cvvLength;

  // Fetch payment link data
  useEffect(() => {
    if (!token) {
      setErrorMessage('Invalid payment link.');
      setStep('error');
      return;
    }

    const fetchLink = async () => {
      try {
        const data = await PaymentLinkAPI.getForCustomer(token);
        if (data.status === 'COMPLETED') {
          setErrorMessage('This payment has already been completed.');
          setStep('expired');
          return;
        }
        if (data.status === 'CANCELLED' || data.status === 'EXPIRED') {
          setErrorMessage('This payment link is no longer valid.');
          setStep('expired');
          return;
        }
        if (new Date(data.expiry_at) < new Date()) {
          setErrorMessage('This payment link has expired.');
          setStep('expired');
          return;
        }
        // QuickClick hand-off: if enabled, POST the signed form to NMI's hosted
        // checkout and let the browser navigate there (card entry happens on NMI).
        if (data.quickclick && data.quickclick.fields && data.quickclick.cart_url) {
          const form = document.createElement('form');
          form.method = 'POST';
          form.action = data.quickclick.cart_url;
          Object.entries(data.quickclick.fields).forEach(([k, v]) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = k;
            input.value = String(v);
            form.appendChild(input);
          });
          document.body.appendChild(form);
          form.submit();
          return;
        }
        setLinkData(data);
        setStep('form');
      } catch (err: any) {
        setErrorMessage(err.message || 'Payment link not found.');
        setStep('error');
      }
    };

    fetchLink();
  }, [token]);

  /**
   * Submit payment to NMI with 3DS data (or without if 3DS not enabled/failed gracefully).
   */
  const submitPayment = useCallback(async (threeDsFields?: {
    cavv: string;
    eci: string;
    xid: string;
    version: string;
    status: string;
    serverTransId: string;
  }) => {
    if (!token) return;

    setStep('processing');
    setStatusMessage('Processing your payment...');

    try {
      const payData: any = {
        ccnumber: cardRef.current.ccnumber,
        ccexp: cardRef.current.ccexp,
        cvv: cardRef.current.cvv,
      };

      if (threeDsFields) {
        payData.three_ds_cavv = threeDsFields.cavv;
        payData.three_ds_eci = threeDsFields.eci;
        payData.three_ds_xid = threeDsFields.xid;
        payData.three_ds_version = threeDsFields.version;
        payData.three_ds_status = threeDsFields.status;
        payData.three_ds_server_trans_id = threeDsFields.serverTransId;
      }

      const result: CustomerPayResponse = await PaymentLinkAPI.customerPay(token, payData);

      if (result.success) {
        setTransactionId(result.transaction_id || '');
        setStep('success');
      } else {
        setErrorMessage(result.message || 'Payment failed. Please try again.');
        setStep('error');
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Payment processing failed. Please try again.');
      setStep('error');
    }
  }, [token]);

  /**
   * Step 6-7: Get 3DS result after challenge and submit payment.
   */
  const handleAuthResult = useCallback(async (transId: string) => {
    setStep('processing');
    setStatusMessage('Verifying authentication...');

    try {
      const result = await ThreeDSAPI.brwResult(transId);
      const transStatus = result.transStatus;

      if (transStatus === 'Y' || transStatus === 'A') {
        // Authenticated — submit payment with 3DS data
        await submitPayment({
          cavv: result.authenticationValue || '',
          eci: result.eci || '',
          xid: result.dsTransID || result.threeDSServerTransID || '',
          version: result.messageVersion || '2.2.0',
          status: transStatus,
          serverTransId: transId,
        });
      } else {
        setErrorMessage(`3DS authentication was not successful (status: ${transStatus}). Please try again.`);
        setStep('error');
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to retrieve 3DS result.');
      setStep('error');
    }
  }, [submitPayment]);

  /**
   * Step 4-5: Execute auth after browser info collection, handle result.
   */
  const executeAuth = useCallback(async (transId: string) => {
    // Prevent double auth calls (timeout + postMessage race)
    if (authCalledRef.current) return;
    authCalledRef.current = true;

    setStep('processing');
    setStatusMessage('Authenticating with your bank...');

    // Collect browser info from JS — must match what ActiveServer's iframe collected
    // (same browser context, so values will be identical).
    // 3DS spec only allows: 1, 4, 8, 15, 16, 24, 32, 48
    const allowedDepths = [1, 4, 8, 15, 16, 24, 32, 48];
    const rawDepth = window.screen.colorDepth || 24;
    const colorDepth = allowedDepths.reduce((prev, curr) =>
      Math.abs(curr - rawDepth) < Math.abs(prev - rawDepth) ? curr : prev
    );
    const browserInfo: Record<string, any> = {
      browserAcceptHeader: 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
      browserJavascriptEnabled: true,
      browserJavaEnabled: false,
      browserLanguage: navigator.language || 'en-US',
      browserColorDepth: String(colorDepth),
      browserScreenHeight: String(window.screen.height || 900),
      browserScreenWidth: String(window.screen.width || 1440),
      browserTZ: String(new Date().getTimezoneOffset()),
      browserUserAgent: navigator.userAgent,
    };

    try {
      const authResult = await ThreeDSAPI.brwAuth({
        threeDSServerTransID: transId,
        browserInfo,
      });

      const transStatus = authResult.transStatus;

      if (transStatus === 'Y' || transStatus === 'A') {
        // Frictionless — authenticated without challenge
        await submitPayment({
          cavv: authResult.authenticationValue || '',
          eci: authResult.eci || '',
          xid: authResult.dsTransID || authResult.threeDSServerTransID || '',
          version: authResult.messageVersion || '2.2.0',
          status: transStatus,
          serverTransId: transId,
        });
      } else if (transStatus === 'C') {
        // Challenge required — load challengeUrl directly in iframe
        // ActiveServer v2 returns a ready-made challengeUrl (no CReq posting needed)
        const challengeUrl = authResult.challengeUrl;
        const resultMonUrl = authResult.resultMonUrl;

        if (challengeUrl) {
          setStep('challenge');
          setTimeout(() => {
            if (challengeIframeRef.current) {
              challengeIframeRef.current.src = challengeUrl;
            }
          }, 100);

          // Fallback: poll resultMonUrl for challenge completion
          // (in case postMessage callback doesn't fire)
          if (resultMonUrl) {
            const pollInterval = setInterval(async () => {
              try {
                const monResp = await fetch(resultMonUrl);
                const monData = await monResp.json();
                if (monData.event === 'AuthResultReady') {
                  clearInterval(pollInterval);
                  await handleAuthResult(transId);
                }
              } catch {
                // Ignore polling errors, keep trying
              }
            }, 3000);
            // Stop polling after 5 minutes
            setTimeout(() => clearInterval(pollInterval), 300000);
          }
        } else {
          setErrorMessage('3DS challenge data missing from authentication response.');
          setStep('error');
        }
      } else if (transStatus === 'D') {
        // Decoupled authentication — cardholder must complete verification
        // via their banking app, SMS, or other out-of-band method.
        setStep('decoupled');
        setStatusMessage('Your bank is requesting verification. Please check your banking app or phone.');

        // Poll for result completion (cardholder completes challenge async)
        const pollDecoupled = async () => {
          const maxWait = 300000; // 5 minutes
          const interval = 5000; // 5 seconds
          let elapsed = 0;

          while (elapsed < maxWait) {
            await new Promise(resolve => setTimeout(resolve, interval));
            elapsed += interval;

            try {
              const result = await ThreeDSAPI.brwResult(transId);
              const finalStatus = result.transStatus;

              if (finalStatus === 'Y' || finalStatus === 'A') {
                await submitPayment({
                  cavv: result.authenticationValue || '',
                  eci: result.eci || '',
                  xid: result.dsTransID || result.threeDSServerTransID || '',
                  version: result.messageVersion || '2.2.0',
                  status: finalStatus,
                  serverTransId: transId,
                });
                return;
              } else if (finalStatus === 'N' || finalStatus === 'R') {
                setErrorMessage('Your bank declined the authentication. Please try a different card.');
                setStep('error');
                return;
              } else if (finalStatus === 'U') {
                setErrorMessage('3DS verification is unavailable. Please try again later or use a different card.');
                setStep('error');
                return;
              }
              // Still D — keep polling
            } catch {
              // Ignore polling errors, keep trying
            }
          }

          // Timeout
          setErrorMessage('Bank verification timed out. Please try again.');
          setStep('error');
        };

        pollDecoupled();
      } else if (transStatus === 'N' || transStatus === 'R') {
        setErrorMessage('Your card issuer declined the 3DS authentication. Please try a different card.');
        setStep('error');
      } else {
        // transStatus U or other — 3DS unavailable, block payment
        setErrorMessage('3DS verification is unavailable for this card. Please try again later or use a different card.');
        setStep('error');
      }
    } catch (err: any) {
      setErrorMessage(err.message || '3DS authentication failed.');
      setStep('error');
    }
  }, [submitPayment]);

  /**
   * Listen for postMessage events from 3DS iframes (hidden method iframe + challenge iframe).
   */
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      const data = event.data;
      if (!data || data.type !== '3ds_event') return;

      const callbackType = data.callbackType;
      const transId = data.transID || serverTransId;

      console.log('[3DS] Event received:', callbackType, 'transID:', transId);

      if (callbackType === '3DSMethodFinished' || callbackType === '3DSMethodSkipped' || callbackType === 'InitAuthTimedOut') {
        // Browser info collection complete → proceed to auth
        await executeAuth(transId);
      } else if (callbackType === 'AuthResultReady') {
        // Challenge completed → get result
        await handleAuthResult(transId);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [serverTransId, executeAuth, handleAuthResult]);

  const validateCard = (): boolean => {
    const errors: Record<string, string> = {};
    const digits = ccnumber.replace(/\D/g, '');
    const cfg = getCardConfig(digits);

    if (!digits || digits.length !== cfg.maxLength) {
      errors.ccnumber = 'Please enter a valid card number';
    }
    if (!ccexp || ccexp.length !== 4) {
      errors.ccexp = 'Expiry must be MM/YY';
    } else {
      const month = parseInt(ccexp.substring(0, 2), 10);
      if (month < 1 || month > 12) {
        errors.ccexp = 'Invalid expiry month';
      }
    }
    if (!cvv || cvv.length !== cfg.cvvLength) {
      errors.cvv = `CVV must be ${cfg.cvvLength} digits`;
    }

    setCardErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Main submit handler — starts 3DS flow or direct payment.
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateCard() || !token || !linkData) return;

    setStep('processing');
    setErrorMessage('');
    setStatusMessage('Initializing secure authentication...');
    authCalledRef.current = false; // Reset for new submission

    // Link issued with 3DS off (per-link toggle): backend marked it exempt — submit directly.
    if (linkData.skip_3ds) {
      await submitPayment();
      return;
    }

    const cardDigits = ccnumber.replace(/\D/g, '');
    // Convert MMYY → YYMM for 3DS cardExpiryDate
    const cardExpiryYYMM = ccexp.length === 4 ? ccexp.substring(2) + ccexp.substring(0, 2) : '';

    try {
      // Convert amount to cents string for ActiveServer
      const amountCents = Math.round(linkData.amount * 100).toString();

      // Step 1: Initialize 3DS
      const initResult = await ThreeDSAPI.brwInit({
        acctNumber: cardDigits,
        cardExpiryDate: cardExpiryYYMM,
        purchaseAmount: amountCents,
        purchaseCurrency: '840', // USD
        // Lets the backend resolve the link's processor → nmi_account so 3DS
        // hits the same merchant the eventual NMI capture will route to.
        payment_link_token: token,
      });

      const sTransId = initResult.threeDSServerTransID;
      const callbackUrl = initResult.threeDSServerCallbackUrl;

      if (!sTransId) {
        // 3DS not available — block payment
        console.error('[3DS] No serverTransID returned, cannot proceed without 3DS');
        setErrorMessage('3DS secure verification could not be initialized. Please try again or use a different card.');
        setStep('error');
        return;
      }

      setServerTransId(sTransId);

      // skipAutoBrowserInfoCollect is set in init, so ActiveServer won't collect
      // browser info via iframe. We provide it directly in the auth call.
      // Go straight to auth with browser info collected from JS.
      await executeAuth(sTransId);
    } catch (err: any) {
      // 3DS init failed — block payment, no fallback to non-3DS
      console.error('[3DS] Init failed:', err.message);
      setErrorMessage('3DS secure verification is currently unavailable. Please try again later.');
      setStep('error');
    }
  };

  const formatAmount = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  return (
    <div className="customer-payment-page">
      <div className="customer-payment-container">
        <div className="payment-header">
          <h1>Secure Payment</h1>
          <div className="lock-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
        </div>

        {step === 'loading' && (
          <div className="payment-loading">
            <div className="spinner"></div>
            <p>Loading payment details...</p>
          </div>
        )}

        {step === 'expired' && (
          <div className="payment-status expired">
            <div className="status-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="48" height="48">
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            </div>
            <h2>Link Unavailable</h2>
            <p>{errorMessage}</p>
          </div>
        )}

        {step === 'error' && (
          <div className="payment-status error">
            <div className="status-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="48" height="48">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h2>Payment Error</h2>
            <p>{errorMessage}</p>
            <button className="retry-btn" onClick={() => { setStep('form'); setErrorMessage(''); authCalledRef.current = false; }}>
              Try Again
            </button>
          </div>
        )}

        {step === 'form' && linkData && (
          <>
            <div className="payment-summary">
              <div className="summary-row">
                <span className="summary-label">Amount Due</span>
                <span className="summary-amount">{formatAmount(linkData.amount, linkData.currency)}</span>
              </div>
              {linkData.order_description && (
                <div className="summary-row">
                  <span className="summary-label">Description</span>
                  <span className="summary-value">{linkData.order_description}</span>
                </div>
              )}
              {(linkData.first_name || linkData.last_name) && (
                <div className="summary-row">
                  <span className="summary-label">Customer</span>
                  <span className="summary-value">{linkData.first_name} {linkData.last_name}</span>
                </div>
              )}
            </div>

            <form onSubmit={handleSubmit} className="card-form">
              <div className="form-group">
                <label>Card Number</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={ccnumber}
                  onChange={(e) => setCcnumber(formatCardNumber(e.target.value))}
                  placeholder="1234 5678 9012 3456"
                  maxLength={19}
                  className={cardErrors.ccnumber ? 'error-field' : ''}
                  autoComplete="cc-number"
                />
                {cardErrors.ccnumber && <span className="error-text">{cardErrors.ccnumber}</span>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Expiry (MM/YY)</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={formatExpiryDisplay(ccexp)}
                    onChange={(e) => setCcexp(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    placeholder="MM/YY"
                    maxLength={5}
                    className={cardErrors.ccexp ? 'error-field' : ''}
                    autoComplete="cc-exp"
                  />
                  {cardErrors.ccexp && <span className="error-text">{cardErrors.ccexp}</span>}
                </div>
                <div className="form-group">
                  <label>CVV</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={cvv}
                    onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').slice(0, cvvMaxLength))}
                    placeholder={cvvMaxLength === 4 ? '1234' : '123'}
                    maxLength={cvvMaxLength}
                    className={cardErrors.cvv ? 'error-field' : ''}
                    autoComplete="cc-csc"
                  />
                  {cardErrors.cvv && <span className="error-text">{cardErrors.cvv}</span>}
                </div>
              </div>

              <button type="submit" className="pay-btn">
                Pay {formatAmount(linkData.amount, linkData.currency)}
              </button>
            </form>

            <div className="security-notice">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <span>Your payment information is encrypted and secure.</span>
            </div>
          </>
        )}

        {(step === 'processing' || step === '3ds_method') && (
          <div className="payment-loading">
            <div className="spinner"></div>
            <p>{statusMessage || 'Processing your payment...'}</p>
            <p className="processing-subtext">Please do not close this page.</p>
          </div>
        )}

        {step === 'challenge' && (
          <div className="challenge-container">
            <p className="challenge-info">Your bank requires additional verification. Please complete the verification below.</p>
            <iframe
              ref={challengeIframeRef}
              title="3DS Challenge"
              className="challenge-iframe"
            />
          </div>
        )}

        {step === 'decoupled' && (
          <div className="payment-loading decoupled-waiting">
            <div className="decoupled-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="48" height="48">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </div>
            <h2>Waiting for Bank Verification</h2>
            <div className="spinner"></div>
            <p>{statusMessage || 'Please complete the verification on your banking app or phone.'}</p>
            <p className="processing-subtext">This may take a few minutes. Please do not close this page.</p>
          </div>
        )}

        {step === 'success' && (
          <div className="payment-status success">
            <div className="status-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="48" height="48">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <h2>Payment Successful!</h2>
            <p>Your payment has been processed successfully.</p>
            {transactionId && (
              <p className="transaction-id">Transaction ID: {transactionId}</p>
            )}
          </div>
        )}

        {/* Hidden iframe for 3DS Method / browser info collection */}
        <iframe
          ref={methodIframeRef}
          title="3DS Method"
          style={{ width: 0, height: 0, border: 'none', position: 'absolute', left: -9999 }}
        />
      </div>

      <style>{`
        .customer-payment-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          color-scheme: light;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding: 1rem;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .customer-payment-container {
          background: white;
          border-radius: 16px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
          padding: 2.5rem;
          width: 100%;
          max-width: 460px;
          position: relative;
        }

        .payment-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .payment-header h1 {
          margin: 0 0 0.5rem 0;
          font-size: 1.5rem;
          color: #1a1a2e;
        }

        .lock-icon {
          color: #10b981;
        }

        .payment-loading {
          text-align: center;
          padding: 3rem 0;
        }

        .spinner {
          border: 4px solid #e5e7eb;
          border-top: 4px solid #6366f1;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          animation: spin 1s linear infinite;
          margin: 0 auto 1rem;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .payment-loading p {
          color: #6b7280;
          margin: 0.5rem 0;
        }

        .processing-subtext {
          font-size: 0.875rem;
          color: #4b5563 !important;
        }

        .payment-summary {
          background: #f9fafb;
          border-radius: 12px;
          padding: 1.25rem;
          margin-bottom: 1.5rem;
        }

        .summary-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.5rem 0;
        }

        .summary-row + .summary-row {
          border-top: 1px solid #e5e7eb;
        }

        .summary-label {
          color: #6b7280;
          font-size: 0.875rem;
        }

        .summary-amount {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1a1a2e;
        }

        .summary-value {
          color: #374151;
          font-weight: 500;
        }

        .card-form .form-group {
          margin-bottom: 1rem;
        }

        .card-form label {
          display: block;
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
          margin-bottom: 0.375rem;
        }

        .card-form input {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          font-size: 1rem;
          background: #ffffff;
          color: #1a1a2e;
          transition: border-color 0.2s;
          box-sizing: border-box;
        }

        .card-form input::placeholder {
          color: #6b7280;
          opacity: 1;
        }

        .card-form input:focus {
          outline: none;
          border-color: #6366f1;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
        }

        .card-form input.error-field {
          border-color: #ef4444;
        }

        .error-text {
          color: #ef4444;
          font-size: 0.75rem;
          margin-top: 0.25rem;
          display: block;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        .pay-btn {
          width: 100%;
          padding: 0.875rem;
          background: #6366f1;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 1.1rem;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s;
          margin-top: 0.5rem;
        }

        .pay-btn:hover {
          background: #5855eb;
        }

        .pay-btn:active {
          transform: scale(0.99);
        }

        .security-notice {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          justify-content: center;
          margin-top: 1.25rem;
          color: #4b5563;
          font-size: 0.8rem;
        }

        .payment-status {
          text-align: center;
          padding: 2rem 0;
        }

        .status-icon {
          margin-bottom: 1rem;
        }

        .payment-status.success .status-icon {
          color: #10b981;
        }

        .payment-status.error .status-icon {
          color: #ef4444;
        }

        .payment-status.expired .status-icon {
          color: #6b7280;
        }

        .payment-status h2 {
          margin: 0 0 0.5rem 0;
          color: #1a1a2e;
        }

        .payment-status p {
          color: #6b7280;
          margin: 0.25rem 0;
        }

        .transaction-id {
          font-family: monospace;
          font-size: 0.85rem;
          color: #4b5563 !important;
          margin-top: 1rem !important;
        }

        .retry-btn {
          margin-top: 1.5rem;
          padding: 0.625rem 1.5rem;
          background: #6366f1;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 0.95rem;
          font-weight: 500;
          cursor: pointer;
        }

        .retry-btn:hover {
          background: #5855eb;
        }

        .decoupled-waiting {
          padding: 2rem 0;
        }

        .decoupled-waiting h2 {
          color: #1a1a2e;
          margin: 0 0 1rem 0;
          font-size: 1.2rem;
        }

        .decoupled-icon {
          color: #6366f1;
          margin-bottom: 1rem;
        }

        .challenge-container {
          padding: 1rem 0;
        }

        .challenge-info {
          color: #6b7280;
          font-size: 0.9rem;
          text-align: center;
          margin-bottom: 1rem;
        }

        .challenge-iframe {
          width: 100%;
          height: 400px;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
        }

        @media (max-width: 480px) {
          .customer-payment-container {
            padding: 1.5rem;
          }

          .form-row {
            grid-template-columns: 1fr;
          }

          .summary-amount {
            font-size: 1.25rem;
          }
        }
      `}</style>
    </div>
  );
};
